import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import Section, { ISection } from "../../model/formconfiguration/Section";
import { IFormType } from "../../model/formconfiguration/FormType";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import CreateFormSectionInteractor from "../../interactor/formconfiguration/CreateFormSectionInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import UpdateFormSectionInteractor from "../../interactor/formconfiguration/UpdateFormSectionInteractor";
import InvalidateFormSectionInteractor from "../../interactor/formconfiguration/InvalidateFormSectionInteractor";
import * as Constants from "../../common/Constants";
import Qualifier, { IQualifier } from "../../model/formconfiguration/Qualifier";
import CreateQualifierInteractor from "../../interactor/formconfiguration/CreateQualifierInteractor";
import UpdateQualifierInteractor from "../../interactor/formconfiguration/UpdateQualifierInteractor";
import InvalidateQualifierInteractor from "../../interactor/formconfiguration/InvalidateQualifierInteractor";
import Attribute, { IAttribute } from "../../model/formconfiguration/Attribute";
import CreateAttributeInteractor from "../../interactor/formconfiguration/CreateAttributeInteractor";
import UpdateAttributeInteractor from "../../interactor/formconfiguration/UpdateAttributeInteractor";
import InvalidateAttributeInteractor from "../../interactor/formconfiguration/InvalidateAttributeInteractor";
import GetFormConfigDetailsInteractor from "../../interactor/formconfiguration/GetFormConfigDetailsInteractor";
import FormConfigQualifierAttribute, {
  IFormConfigQualifierAttribute
} from "../../service/formconfiguration/FormConfigQualifierAttribute";
import FormConfigSectionQualifier, {
  IFormConfigSectionQualifier
} from "../../model/formconfiguration/FormConfigSectionQualifier";
import UpdateFormConfigSectionQualifierInteractor from "../../interactor/formconfiguration/UpdateFormConfigSectionQualifierInteractor";
import CreateFormConfigSectionQualifierInteractor from "../../interactor/formconfiguration/CreateFormConfigSectionQualifierInteractor";
import CreateFormConfigQualifierAttributeInteractor from "../../interactor/formconfiguration/CreateFormConfigQualifierAttributeInteractor";
import UpdateFormConfigQualifierAttributeInteractor from "../../interactor/formconfiguration/UpdateFormConfigQualifierAttributeInteractor";

export interface TFormConfigurationPresenter extends TLoadingAwarePresenter {
  formSections?: ISection[];
  formType: IFormType;
  selectedFormElement?: any;
  qualifiers?: IQualifier[];
  attributes?: IAttribute[];
  formParamsBoxVisible: boolean;
}

export interface IFormConfigurationPresenter
  extends TFormConfigurationPresenter,
    TPresentable {
  loadFormDetails(
    formType: IFormType,
    formSections?: ISection[],
    qualifiers?: IQualifier[],
    attributes?: IAttribute[]
  ): void;
  setFormSections(section?: ISection[]): void;
  setFormType(formType: IFormType): void;
  setQualifiers(qualifiers?: IQualifier[]): void;
  createFormSection(parentSection: ISection): void;
  updateFormSection(section: ISection): void;
  invalidateFormSection(formSectionId: number): void;
  setSelectedFormElement(selectedFormElement: ISection): void;
  createFormConfigSectionQualifier(
    parentSection: ISection,
    qualifier: IFormConfigSectionQualifier
  ): void;
  updateFormConfigSectionQualifier(
    formConfigSectionQualifier: IFormConfigSectionQualifier
  ): void;
  invalidateQualifier(id: number): void;
  createFormConfigQualifierAttribute(
    parentFormConifgSectionQualifier: IFormConfigSectionQualifier,
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): Promise<void>;
  setAttributes(attributes?: IAttribute[]): void;
  updateFormConfigQualifierAttribute(
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): void;
  invalidateAttribute(attribute: IFormConfigQualifierAttribute): void;
  isSelectedComponent(formElement: ISection | IQualifier | IAttribute): boolean;
  hideFormParamsBox(): void;
}
const defaultState: TFormConfigurationPresenter = {
  formSections: [],
  formType: {} as IFormType,
  selectedFormElement: undefined,
  qualifiers: [],
  attributes: [],
  formParamsBoxVisible: false
};

const FormConfigurationPresenter = withStore<
  IFormConfigurationPresenter,
  TFormConfigurationPresenter
>(({ application, store, loader, translate }): IFormConfigurationPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TFormConfigurationPresenter>();

  loader.start("formConfigurationLoader");

  const loadFormDetails = (
    formType: IFormType,
    formSections?: ISection[],
    qualifiers?: IQualifier[],
    attributes?: IAttribute[]
  ) => {
    setFormSections(formSections);
    setFormType(formType);
    setQualifiers(qualifiers);
    setAttributes(attributes);
    loader.stop("formConfigurationLoader");
  };

  const setFormSections = (formSections?: ISection[]) => {
    _store.update({ formSections });
  };

  const setFormType = (formType: IFormType) => {
    _store.update({ formType });
  };

  const setQualifiers = (qualifiers?: IQualifier[]) => {
    _store.update({ qualifiers });
  };

  const setAttributes = (attributes?: IAttribute[]) => {
    _store.update({ attributes });
  };

  const setSelectedFormElement = (newSelectedFormElement?: any) => {
    let { selectedFormElement } = _store.getState();
    _store.update({
      formParamsBoxVisible: true
    });
    if (selectedFormElement !== newSelectedFormElement) {
      _store.update({
        selectedFormElement: newSelectedFormElement
      });
    }
  };

  const removeSelectedFormElement = () => {
    _store.update({ selectedFormElement: undefined });
  };

  const getFormConfigDetails = async () => {
    const { formType } = _store.getState<IFormConfigurationPresenter>();

    let { formSections, qualifiers, attributes } = await _application.container
      .resolve<GetFormConfigDetailsInteractor>("getFormConfigDetails")
      .execute(formType.code);
    _store.update({ formSections, qualifiers, attributes });
  };

  const getSectionOrderNumber = (parentSection?: ISection) => {
    const { formSections } = _store.getState();
    let currentHighestOrderNumber = 0;
    if (parentSection) {
      if (parentSection && parentSection.subsections) {
        parentSection.subsections.forEach((section: ISection) => {
          if (section.orderNumber > currentHighestOrderNumber) {
            currentHighestOrderNumber = section.orderNumber;
          }
        });
      }
    } else {
      formSections.forEach((section: ISection) => {
        if (
          section.subsections.length === 0 &&
          section.orderNumber > currentHighestOrderNumber
        ) {
          currentHighestOrderNumber = section.orderNumber;
        }
      });
    }

    return currentHighestOrderNumber + 1;
  };

  const createFormSection = async (parentSection?: ISection) => {
    try {
      loader.start("updateFormConfigurationLoader");
      let newFormSection = getDefaultFormSection(parentSection);
      newFormSection.required = true;
      await _application.container
        .resolve<CreateFormSectionInteractor>("createFormSection")
        .execute(newFormSection);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("section_form.messages.create_section_success"));
      await getFormConfigDetails();
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };
  const getNameTimestamp = (name: string) => {
    const today = new Date();
    return `${name}${
      today.getFullYear() +
      String(today.getMonth()).padStart(2, "0") +
      String(today.getDate()).padStart(2, "0") +
      String(today.getHours()).padStart(2, "0") +
      String(today.getMinutes()).padStart(2, "0") +
      String(today.getSeconds()).padStart(2, "0")
    }`;
  };

  const getDefaultFormSection = (parentSection?: ISection) => {
    const { formType } = _store.getState();
    const orderNumber = getSectionOrderNumber(parentSection);

    return Section({
      id: 0,
      valid: true,
      orderNumber: orderNumber,
      orderNumberVisible:
        Constants.FormSectionDefualtValues.ORDER_NUMBER_VISIBLE,
      name: getNameTimestamp(Constants.FormSectionDefualtValues.NAME),
      label: getNameTimestamp(Constants.FormSectionDefualtValues.LABEL),
      labelVisible: Constants.FormSectionDefualtValues.LABEL_VISIBLE,
      formTypeCode: formType.code,
      description: Constants.FormSectionDefualtValues.DESCRIPTION,
      comment: Constants.FormSectionDefualtValues.COMMENT,
      formSectionId: parentSection ? parentSection.id : undefined,
      sqlValidation: Constants.FormSectionDefualtValues.SQL_VALIDATION,
      maxNumber: Constants.FormSectionDefualtValues.MAX_NUMBER,
      subsections: Constants.FormSectionDefualtValues.SUBSECTIONS,
      qualifiers: Constants.FormSectionDefualtValues.QUALIFIERS,
      required: Constants.FormSectionDefualtValues.REQUIRED,
      portalAggregate: Constants.FormSectionDefualtValues.PORTAL_AGGREGATE
    });
  };

  const updateFormSection = async (section: ISection) => {
    try {
      loader.start("updateFormConfigurationLoader");
      const { formType } = _store.getState();
      section.formTypeCode = formType.code;
      await _application.container
        .resolve<UpdateFormSectionInteractor>("updateFormSection")
        .execute(section);
      await getFormConfigDetails();
      setSelectedFormElement(section);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("section_form.messages.update_section_success"));
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const invalidateFormSection = async (formSectionId: number) => {
    try {
      loader.start("updateFormConfigurationLoader");
      const { formType } = _store.getState();
      await _application.container
        .resolve<InvalidateFormSectionInteractor>("invalidateFormSection")
        .execute(formType.code, formSectionId);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("section_form.messages.remove_section_success"));
      await getFormConfigDetails();
      _store.update({ selectedFormElement: undefined });
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createFormConfigSectionQualifier = async (
    parentFormSection: ISection,
    formConfigSectionQualifier: IFormConfigSectionQualifier
  ): Promise<void> => {
    try {
      loader.start("updateFormConfigurationLoader");

      const { formType } = _store.getState<IFormConfigurationPresenter>();

      formConfigSectionQualifier = fillDefaultFormConfigSectionQualifier(
        parentFormSection,
        formConfigSectionQualifier
      );

      const qualifier = formConfigSectionQualifier.qualifier;

      if (qualifier.name) {
        await _application.container
          .resolve<CreateQualifierInteractor>("createQualifier")
          .execute(qualifier, formType.code);
      }

      await _application.container
        .resolve<CreateFormConfigSectionQualifierInteractor>(
          "createFormConfigSectionQualifier"
        )
        .execute(
          formType.code,
          formConfigSectionQualifier.formSectionId,
          formConfigSectionQualifier
        );
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("qualifier_form.messages.create_qualifier_success"));

      await getFormConfigDetails();
      updateSelectedSection();

      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const getQualifierOrderNumber = (parentSection: ISection) => {
    let currentHighestOrderNumber = 0;

    parentSection.qualifiers &&
      parentSection.qualifiers.forEach((qualifier) => {
        if (qualifier.orderNumber > currentHighestOrderNumber) {
          currentHighestOrderNumber = qualifier.orderNumber;
        }
      });
    return currentHighestOrderNumber + 1;
  };

  const fillDefaultFormConfigSectionQualifier = (
    parentFormSection: ISection,
    formConfigSectionQualifier: IFormConfigSectionQualifier
  ): IFormConfigSectionQualifier => {
    return FormConfigSectionQualifier({
      ...formConfigSectionQualifier,
      formSectionId: parentFormSection.id,
      orderNumberVisible: Constants.QualifierDefaultValues.ORDER_NUMBER_VISIBLE,
      labelVisible: Constants.QualifierDefaultValues.LABEL_VISIBLE,
      orderNumber: getQualifierOrderNumber(parentFormSection),
      visiblePublic: Constants.QualifierDefaultValues.VISIBLE_PUBLIC,
      valid: Constants.QualifierDefaultValues.VALID,
      required: Constants.QualifierDefaultValues.REQUIRED,
      qualifier: fillDetfaultQualifier(formConfigSectionQualifier.qualifier)
    });
  };

  const fillDetfaultQualifier = (qualifier: IQualifier): IQualifier => {
    return Qualifier({
      ...qualifier,
      qualifierGroupCode: Constants.QualifierDefaultValues.QUALIFIER_GROUP_CODE
    });
  };

  const updateFormConfigSectionQualifier = async (
    formConfigSectionQualifier: IFormConfigSectionQualifier
  ) => {
    try {
      const { formType } = _store.getState<IFormConfigurationPresenter>();
      loader.start("updateFormConfigurationLoader");

      const qualifier = formConfigSectionQualifier.qualifier;
      await _application.container
        .resolve<UpdateQualifierInteractor>("updateQualifier")
        .execute(qualifier.code, qualifier);

      await _application.container
        .resolve<UpdateFormConfigSectionQualifierInteractor>(
          "updateFormConfigSectionQualifier"
        )
        .execute(
          formType.code,
          formConfigSectionQualifier.id,
          formConfigSectionQualifier.formSectionId,
          formConfigSectionQualifier
        );
      await getFormConfigDetails();
      setSelectedFormElement(formConfigSectionQualifier);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("qualifier_form.messages.update_qualifier_success"));
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const invalidateQualifier = async (id: number) => {
    try {
      loader.start("updateFormConfigurationLoader");
      await _application.container
        .resolve<InvalidateQualifierInteractor>("invalidateQualifier")
        .execute(id);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("qualifier_form.messages.delete_qualifier_success"));
      await getFormConfigDetails();
      removeSelectedFormElement();
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createFormConfigQualifierAttribute = async (
    parentFormConifgSectionQualifier: IFormConfigSectionQualifier,
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ) => {
    try {
      loader.start("updateFormConfigurationLoader");

      const { formType } = _store.getState<IFormConfigurationPresenter>();

      formConfigQualifierAttribute = fillDefaultFormConfigQualifierAttribute(
        parentFormConifgSectionQualifier,
        formConfigQualifierAttribute
      );

      const attribute = formConfigQualifierAttribute.attribute;

      if (attribute.attributeName) {
        await _application.container
          .resolve<CreateAttributeInteractor>("createAttribute")
          .execute(attribute, formType.code);
      }

      await _application.container
        .resolve<CreateFormConfigQualifierAttributeInteractor>(
          "createFormConfigQualifierAttribute"
        )
        .execute(
          formConfigQualifierAttribute.qualifierCode,
          formConfigQualifierAttribute
        );
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("attribute_form.messages.create_attribute_success"));

      await getFormConfigDetails();
      updateSelectedFormSectionQualifier();

      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const getAttributeOrderNumberInsertDisplay = (
    parentFormConfigSectionQualifier: IFormConfigSectionQualifier
  ): number => {
    let currentHighestOrderNumber = 0;

    (parentFormConfigSectionQualifier.attributes || []).forEach((attribute) => {
      if (attribute.orderNumberInsertDisplay > currentHighestOrderNumber) {
        currentHighestOrderNumber = attribute.orderNumberInsertDisplay;
      }
    });
    return currentHighestOrderNumber + 1;
  };

  const fillDefaultFormConfigQualifierAttribute = (
    parentFormConfigSectionQualifier: IFormConfigSectionQualifier,
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): IFormConfigQualifierAttribute => {
    return FormConfigQualifierAttribute({
      ...formConfigQualifierAttribute,
      qualifierCode: parentFormConfigSectionQualifier.qualifierCode,
      required: Constants.AttributeDefaultData.REQUIRED,
      validation: Constants.AttributeDefaultData.VALIDATION,
      orderNumberInsertDisplay: getAttributeOrderNumberInsertDisplay(
        parentFormConfigSectionQualifier
      ),
      visible: Constants.AttributeDefaultData.VISIBLE,
      labelVisible: Constants.AttributeDefaultData.VISIBLE,
      attribute: fillDefaultAttribute(formConfigQualifierAttribute.attribute)
    });
  };

  const fillDefaultAttribute = (attribute: IAttribute): IAttribute => {
    return Attribute({
      ...attribute,
      valid: Constants.AttributeDefaultData.VALID
    });
  };

  const updateFormConfigQualifierAttribute = async (
    formConfigQualifierAttribute: IFormConfigQualifierAttribute
  ): Promise<void> => {
    try {
      loader.start("updateFormConfigurationLoader");
      const attribute = formConfigQualifierAttribute.attribute;
      await _application.container
        .resolve<UpdateAttributeInteractor>("updateAttribute")
        .execute(attribute);

      await _application.container
        .resolve<UpdateFormConfigQualifierAttributeInteractor>(
          "updateFormConfigQualifierAttribute"
        )
        .execute(
          formConfigQualifierAttribute.qualifierCode,
          formConfigQualifierAttribute.attributeCode,
          formConfigQualifierAttribute
        );
      await getFormConfigDetails();
      setSelectedFormElement(formConfigQualifierAttribute);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("attribute_form.messages.update_attribute_success"));
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const invalidateAttribute = async (
    attribute: IFormConfigQualifierAttribute
  ) => {
    try {
      loader.start("updateFormConfigurationLoader");
      await _application.container
        .resolve<InvalidateAttributeInteractor>("invalidateAttribute")
        .execute(attribute.qualifierCode, attribute.attributeCode);
      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(translate("attribute_form.messages.delete_attribute_success"));
      await getFormConfigDetails();
      removeSelectedFormElement();
      loader.stop("updateFormConfigurationLoader");
    } catch (error) {
      loader.stop("updateFormConfigurationLoader");
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const onRenderBreadcrumbLabel = (id: string) => {
    const state = _store.getState<TFormConfigurationPresenter>();
    return state.formType?.name;
  };
  _store.update({
    breadCrumb: {
      ...(_store.getState() as any).breadCrumb,
      formTypeCode: onRenderBreadcrumbLabel
    }
  });

  const isSelectedComponent = (
    formElement: ISection | IQualifier | IAttribute
  ) => {
    const selectedFormElement =
      _store.getState<TFormConfigurationPresenter>().selectedFormElement;
    return (
      selectedFormElement &&
      JSON.stringify(formElement) === JSON.stringify(selectedFormElement)
    );
  };

  const hideFormParamsBox = () => {
    _store.update({ formParamsBoxVisible: false });
  };

  const getFormSection = (
    formSections: ISection[],
    id: number
  ): ISection | undefined => {
    let resultSection: ISection | undefined = undefined;

    for (let formSection of formSections) {
      if (formSection.id === id) {
        resultSection = formSection;
        break;
      }

      if (formSection.subsections.length !== 0) {
        let subsection = getFormSection(formSection.subsections, id);

        if (subsection) {
          resultSection = subsection;
          break;
        }
      }
    }
    return resultSection;
  };

  const updateSelectedSection = () => {
    const { selectedFormElement, formSections } =
      _store.getState<IFormConfigurationPresenter>();

    const selectedSection = getFormSection(
      formSections || ([] as ISection[]),
      selectedFormElement.id
    );

    _store.update({ selectedFormElement: selectedSection });
  };

  const getFormSectionQualifier = (
    formSectionQualifiers: IFormConfigSectionQualifier[],
    id: number
  ): IFormConfigSectionQualifier | undefined => {
    return formSectionQualifiers.find(
      (formSectionQualifier: IFormConfigSectionQualifier) =>
        formSectionQualifier.id === id
    );
  };

  const updateSelectedFormSectionQualifier = () => {
    const { selectedFormElement, formSections } =
      _store.getState<IFormConfigurationPresenter>();

    const parentSection = getFormSection(
      formSections || ([] as ISection[]),
      selectedFormElement.formSectionId
    );

    const selectedFormSectionQualifier = getFormSectionQualifier(
      parentSection?.qualifiers || ([] as IFormConfigSectionQualifier[]),
      selectedFormElement.id
    );

    _store.update({ selectedFormElement: selectedFormSectionQualifier });
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadFormDetails,
    setFormSections,
    setFormType,
    createFormSection,
    updateFormSection,
    invalidateFormSection,
    setSelectedFormElement,
    createFormConfigSectionQualifier,
    setQualifiers,
    updateFormConfigSectionQualifier,
    invalidateQualifier,
    createFormConfigQualifierAttribute,
    setAttributes,
    updateFormConfigQualifierAttribute,
    invalidateAttribute,
    isSelectedComponent,
    hideFormParamsBox
  };
}, defaultState);

export default FormConfigurationPresenter;
